import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Clock,
  FileQuestion,
  TrendingUp,
  Wallet,
  CheckCircle,
  Sparkles,
  Award,
  Target,
  Zap,
  PlayCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { UnifiedHeader } from '../components/UnifiedHeader';
import { UnifiedFooter } from '../components/UnifiedFooter';
import { SEOHead } from '../components/SEOHead';
import { StructuredData } from '../components/StructuredData';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { getCanonicalUrl, SITE_PROVIDER, SITE_BRAND, getSiteUrl } from '../utils/siteConfig';
import { ModernPaymentDialog } from '../components/payment/ModernPaymentDialog';
import { ExamStartConfirmation } from '../components/exam/ExamStartConfirmation';
import { apiService } from '@vsat/shared/lib/api';
import { useAuthStore } from '@vsat/shared/store/authStore';
import { motion } from 'framer-motion';
import {
  trackExamView,
  trackExamStart,
  trackExamResume,
  trackExamBeginCheckout,
  trackExamPurchase,
  initScrollDepthTracking,
} from '../telemetry/googleAnalytics';

interface ExamSummary {
  id: string;
  title: string;
  subject?: string;
  description?: string;
  durationMinutes?: number;
  totalQuestions?: number;
  price?: number;
  level?: string;
  type?: string;
  status?: string;
  isMock?: boolean;
  hasActivePayment?: boolean; // Payment status from backend
}

interface ExamDetailLocationState {
  exam?: ExamSummary;
}

interface ActiveSession {
  sessionId: string;
  status: string;
  remainingTime: number;
  answers?: Record<string, any>;
}

const SUBJECT_LABELS: Record<string, string> = {
  LITERATURE: 'Ngữ văn',
  MATH: 'Toán',
  ENGLISH: 'Tiếng Anh',
  PHYSICS: 'Vật lý',
  CHEMISTRY: 'Hóa học',
  BIOLOGY: 'Sinh học',
  HISTORY: 'Lịch sử',
  GEOGRAPHY: 'Địa lý',
  CIVIC_EDUCATION: 'Giáo dục Công dân',
};

const getSubjectLabel = (subject?: string): string | undefined => {
  if (!subject) return undefined;
  return SUBJECT_LABELS[subject.toUpperCase()] ?? subject;
};

const mapApiExam = (exam: any): ExamSummary => ({
  id: exam?.id,
  title: exam?.title || 'Đề thi',
  subject: exam?.subject || exam?.level,
  description: exam?.description,
  durationMinutes: exam?.duration,
  totalQuestions: exam?.totalQuestions || exam?.questionCount,
  price: exam?.price ?? 0,
  level: exam?.level,
  type: exam?.type || exam?.examType,
  status: exam?.status,
  isMock: false,
  hasActivePayment: exam?.hasActivePayment, // Include payment status from backend
});

export function ExamDetailPage() {
  const { examId } = useParams<{ examId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const initialExam = (location.state as ExamDetailLocationState | undefined)?.exam;
  const { isAuthenticated } = useAuthStore();

  const [exam, setExam] = useState<ExamSummary | null>(initialExam ?? null);
  const [loading, setLoading] = useState(!initialExam);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [activeSession, setActiveSession] = useState<ActiveSession | null>(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [hasPaid, setHasPaid] = useState(false);
  const [preChecking, setPreChecking] = useState(false);

  const isPremium = exam && exam.price && exam.price > 0;
  const alreadyPaid = isPremium && (hasPaid || exam?.hasActivePayment === true);
  const hasActiveSession =
    activeSession && activeSession.sessionId && activeSession.status === 'IN_PROGRESS';

  useEffect(() => {
    if (!examId) return;
    if (initialExam && initialExam.id === examId) {
      setLoading(false);
      return;
    }

    const fetchExam = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiService.getExamForStudent(examId);
        setExam(mapApiExam(data));
      } catch (err) {
        console.error('Failed to fetch exam detail', err);
        setError('Không tải được thông tin đề. Vui lòng thử lại sau.');
      } finally {
        setLoading(false);
      }
    };

    fetchExam();
  }, [examId, initialExam]);

  // Track exam view when exam data is available
  useEffect(() => {
    if (exam && examId) {
      trackExamView({
        exam_id: examId,
        exam_name: exam.title,
        subject: exam.subject,
        is_premium: !!(exam.price && exam.price > 0),
        price: exam.price,
      });
    }
  }, [exam?.id]);

  // Track scroll depth
  useEffect(() => {
    return initScrollDepthTracking();
  }, []);

  // Check for active session (allows resume without new payment)
  useEffect(() => {
    if (!examId) {
      setCheckingSession(false);
      return;
    }

    const checkSession = async () => {
      setCheckingSession(true);
      try {
        const sessionStatus = await apiService.getSessionStatus(examId);
        if (sessionStatus && sessionStatus.sessionId && sessionStatus.status === 'IN_PROGRESS') {
          setActiveSession(sessionStatus);
        }
      } catch (err) {
        console.log('No active session found');
      } finally {
        setCheckingSession(false);
      }
    };

    checkSession();
  }, [examId]);

  const handleResume = () => {
    if (!examId || !activeSession) return;

    trackExamResume(examId);
    toast.success('Đang tiếp tục làm bài...');
    navigate(`/lam-bai/${examId}`, {
      state: {
        resumeSession: activeSession,
        examTitle: exam?.title,
      },
    });
  };

  const handleStartClick = async () => {
    if (!exam || !examId) return;

    if (exam.isMock) {
      toast.error('Đề thi mẫu chưa hỗ trợ làm bài');
      return;
    }

    // Check authentication before allowing exam start or payment
    if (!isAuthenticated) {
      // Save current page for redirect after login
      localStorage.setItem('redirectAfterLogin', `/thi-thu-thpt/${examId}`);
      toast.info('Vui lòng đăng nhập để làm bài');
      navigate('/login');
      return;
    }

    // Pre-check exam generation capability BEFORE checking payment or starting
    // This ensures that if the exam cannot be generated, we don't take money
    try {
      setPreChecking(true);
      await apiService.preCheckExam(examId);
      // Clear any stale session data from previous attempts
      // This prevents issues where localStorage contains old data without questions
      localStorage.removeItem('currentExamSession');
      localStorage.removeItem('currentExamQuestions');
    } catch (err: any) {
      setPreChecking(false);
      const msg =
        err?.response?.data?.message || err.message || 'Hệ thống đang bận. Vui lòng thử lại sau.';
      toast.error(`Không thể chuẩn bị đề thi: ${msg}`);
      return;
    } finally {
      setPreChecking(false);
    }

    // Premium exam -> always check payment status with backend
    if (isPremium) {
      try {
        setCheckingPayment(true);
        const paymentStatus = await apiService.checkExamPaymentStatus(examId);
        const hasPaidStatus = paymentStatus.hasPayment && paymentStatus.payment !== null;

        if (hasPaidStatus) {
          // Already paid -> update state and show confirmation dialog
          setHasPaid(true);
          setShowConfirmDialog(true);
          return;
        }
      } catch (error) {
        // If check fails, show payment dialog
        console.error('Error checking payment status:', error);
      } finally {
        setCheckingPayment(false);
      }

      // Not paid -> show payment dialog
      trackExamBeginCheckout({
        exam_id: examId,
        exam_name: exam.title,
        value: exam.price || 0,
        currency: 'VND',
      });
      setShowPaymentDialog(true);
      return;
    }

    // Free exam -> show confirmation dialog directly
    setShowConfirmDialog(true);
  };

  const handleConfirmStart = async () => {
    if (!exam || !examId) return;

    setStarting(true);
    setError(null);

    try {
      const sessionResponse = await apiService.startExamSession(examId);

      // Store session data - startExam response already contains exam with generated questions
      localStorage.setItem('currentExamSession', JSON.stringify(sessionResponse));
      // Also store in currentExamQuestions for backward compatibility
      localStorage.setItem('currentExamQuestions', JSON.stringify({ exam: sessionResponse.exam }));

      trackExamStart({
        exam_id: examId,
        exam_name: exam.title,
        subject: exam.subject,
        is_premium: !!isPremium,
      });
      toast.success('Bắt đầu làm bài!');
      navigate(`/lam-bai/${examId}`, { state: { examTitle: exam.title.normalize('NFC') } });
    } catch (err: any) {
      console.error('Failed to start exam', err);
      const errorMsg = err?.message || 'Không thể bắt đầu đề thi. Vui lòng thử lại.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setStarting(false);
    }
  };

  const handlePaymentSuccess = () => {
    setShowPaymentDialog(false);
    setHasPaid(true); // Mark as paid to prevent showing payment dialog again
    if (exam && examId) {
      trackExamPurchase({
        exam_id: examId,
        exam_name: exam.title,
        value: exam.price || 0,
        currency: 'VND',
      });
    }
    toast.success('🎉 Đã mở khóa! Bắt đầu luyện thi ngay');
    // Immediately show confirmation dialog to start exam
    setShowConfirmDialog(true);
  };

  if (loading) {
    return (
      <div className='bg-background-light dark:bg-background-dark min-h-screen flex flex-col'>
        <UnifiedHeader />
        <main className='flex-1 pt-20 pb-12 lg:pt-24'>
          <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'>
            <div className='space-y-4 animate-pulse'>
              {/* Hero skeleton */}
              <div className='h-48 bg-amber-50 dark:bg-gray-800 rounded-2xl border border-amber-100 dark:border-gray-700' />
              {/* Benefits skeleton */}
              <div className='rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700'>
                {[...Array(3)].map((_, i) => (
                  <div key={i} className='flex items-center gap-3 px-4 py-3'>
                    <div className='w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg flex-shrink-0' />
                    <div className='flex-1 space-y-1'>
                      <div className='h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3' />
                      <div className='h-2 bg-gray-200 dark:bg-gray-700 rounded w-2/3' />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </main>
        <UnifiedFooter />
      </div>
    );
  }

  if (!exam) {
    return (
      <div className='bg-background-light dark:bg-background-dark min-h-screen flex flex-col'>
        <UnifiedHeader />
        <main className='flex-1 pt-20 pb-12 lg:pt-24'>
          <div className='max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
            <div className='text-center rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 p-8'>
              <h2 className='text-2xl font-bold text-red-600 dark:text-red-400 mb-2'>
                Không tìm thấy đề thi
              </h2>
              <p className='text-gray-600 dark:text-gray-400 mb-6'>
                Vui lòng quay lại danh sách và chọn đề khác
              </p>
              <button
                className='px-6 py-3 rounded-lg bg-primary text-white font-semibold hover:bg-orange-600 transition-colors'
                onClick={() => navigate('/thi-thu-thpt')}
              >
                Quay lại danh sách
              </button>
            </div>
          </div>
        </main>
        <UnifiedFooter />
      </div>
    );
  }

  return (
    <div className='bg-background-light dark:bg-background-dark min-h-screen flex flex-col text-gray-900 dark:text-gray-50'>
      <SEOHead
        title={`${exam.title.normalize('NFC')} | Thi thử THPT Online`}
        description={
          exam.description ||
          `Luyện thi thử THPT môn ${exam.subject || 'tổng hợp'} - ${exam.totalQuestions || 0} câu hỏi, ${exam.durationMinutes || 90} phút. ${isPremium ? `Chỉ ${exam.price?.toLocaleString()}đ` : 'Miễn phí'}.`
        }
        keywords={`thi thử THPT, ${exam.subject || ''}, đề thi thử, luyện thi THPT, ${exam.title.normalize('NFC')}`}
        canonicalUrl={getCanonicalUrl(`/thi-thu-thpt/${examId}`)}
      />

      {/* Course Schema */}
      <StructuredData
        id={`course-${examId}`}
        data={{
          type: 'Course',
          name: exam.title.normalize('NFC'),
          description: exam.description || `Đề thi thử THPT môn ${exam.subject || 'tổng hợp'}`,
          provider: SITE_PROVIDER,
          providerUrl: getSiteUrl(),
          url: getCanonicalUrl(`/thi-thu-thpt/${examId}`),
          subject: exam.subject,
          educationalLevel: 'THPT',
          duration: exam.durationMinutes ? `PT${exam.durationMinutes}M` : undefined,
        }}
      />

      {/* Product Schema for paid exams */}
      {isPremium && (
        <StructuredData
          id={`product-${examId}`}
          data={{
            type: 'Product',
            name: exam.title.normalize('NFC'),
            description:
              exam.description || `Đề thi thử THPT Premium môn ${exam.subject || 'tổng hợp'}`,
            url: getCanonicalUrl(`/thi-thu-thpt/${examId}`),
            brand: SITE_BRAND,
            price: exam.price || 0,
          }}
        />
      )}

      {/* BreadcrumbList Schema */}
      <StructuredData
        id={`breadcrumb-${examId}`}
        data={{
          type: 'BreadcrumbList',
          items: [
            { name: 'Trang chủ', url: getSiteUrl() },
            { name: 'Kho đề thi', url: getCanonicalUrl('/thi-thu-thpt') },
            { name: exam.title.normalize('NFC'), url: getCanonicalUrl(`/thi-thu-thpt/${examId}`) },
          ],
        }}
      />

      <UnifiedHeader />

      {/* Breadcrumbs */}
      <div className='pt-20 lg:pt-24'>
        <Breadcrumbs
          items={[
            { label: 'Trang chủ', href: '/' },
            { label: 'Kho đề thi', href: '/thi-thu-thpt' },
            { label: exam.title.normalize('NFC') },
          ]}
        />
      </div>

      <motion.main
        className='flex-1 pb-12'
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
          {/* Hero Section */}
          <div className='relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-50 via-orange-50 to-white dark:from-gray-800 dark:via-gray-800 dark:to-gray-900 border border-amber-100 dark:border-gray-700 p-5 sm:p-8 mb-6 shadow-sm'>
            {/* Animated Background Decorations */}
            <div className='absolute top-0 right-0 w-96 h-96 bg-amber-200/30 dark:bg-white/5 rounded-full blur-3xl -mr-48 -mt-48 animate-pulse' />
            <div className='absolute bottom-0 left-0 w-72 h-72 bg-orange-200/40 dark:bg-amber-300/10 rounded-full blur-2xl -ml-36 -mb-36' />
            <div
              className='absolute top-1/2 right-1/4 w-24 h-24 bg-amber-100/60 dark:bg-white/10 rounded-full blur-xl animate-bounce'
              style={{ animationDuration: '3s' }}
            />

            <div className='relative z-10'>
              {/* Badges row - mobile friendly */}
              <motion.div
                className='flex flex-wrap gap-2 mb-3'
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 }}
              >
                {isPremium && (
                  <span className='inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-amber-500 text-white shadow-sm'>
                    <Award className='w-3.5 h-3.5' />
                    PREMIUM
                  </span>
                )}
                <span className='inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800'>
                  <CheckCircle className='w-3 h-3 mr-1' />
                  {exam.status || 'ACTIVE'}
                </span>
              </motion.div>

              <motion.div
                className='flex items-center gap-2 mb-3'
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 }}
              >
                <span className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800'>
                  <Sparkles className='w-3 h-3' />
                  THPT Online
                </span>
                {getSubjectLabel(exam.subject) && (
                  <span className='inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-100 dark:border-blue-800'>
                    {getSubjectLabel(exam.subject)}
                  </span>
                )}
              </motion.div>
              <motion.h1
                className='text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3 leading-snug'
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                {exam.title.normalize('NFC')}
              </motion.h1>
              {exam.description && (
                <motion.p
                  className='text-gray-600 dark:text-gray-300 text-sm sm:text-base leading-relaxed max-w-2xl'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  {exam.description}
                </motion.p>
              )}

              {/* Quick Stats in Hero */}
              <motion.div
                className='flex flex-wrap gap-2 sm:gap-4 mt-4'
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <div className='flex items-center gap-1.5 bg-white dark:bg-gray-700 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-600 shadow-sm'>
                  <Clock className='w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0' />
                  <span className='text-gray-700 dark:text-gray-200 font-semibold text-sm'>
                    {exam.durationMinutes || 90} phút
                  </span>
                </div>
                <div className='flex items-center gap-1.5 bg-white dark:bg-gray-700 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-600 shadow-sm'>
                  <FileQuestion className='w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0' />
                  <span className='text-gray-700 dark:text-gray-200 font-semibold text-sm'>
                    {exam.totalQuestions || 0} câu hỏi
                  </span>
                </div>
                {isPremium ? (
                  <div className='flex items-center gap-1.5 bg-amber-500 dark:bg-amber-600 rounded-lg px-3 py-2 shadow-sm'>
                    <Wallet className='w-4 h-4 text-white flex-shrink-0' />
                    <span className='text-white font-bold text-sm'>
                      {exam.price?.toLocaleString()}đ
                    </span>
                  </div>
                ) : (
                  <div className='flex items-center gap-1.5 bg-emerald-500 dark:bg-emerald-600 rounded-lg px-3 py-2 shadow-sm'>
                    <Zap className='w-4 h-4 text-white flex-shrink-0' />
                    <span className='text-white font-bold text-sm'>Miễn phí</span>
                  </div>
                )}
              </motion.div>
            </div>
          </div>

          {/* Benefits  -  compact 3-line */}
          <motion.div
            className='mb-6 rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700 overflow-hidden'
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className='flex items-center gap-3 px-4 py-3'>
              <div className='w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0'>
                <Target className='w-4 h-4 text-orange-500' />
              </div>
              <div>
                <p className='text-sm font-semibold text-gray-900 dark:text-white'>
                  Chuẩn Cấu Trúc
                </p>
                <p className='text-xs text-gray-500 dark:text-gray-400'>
                  Sát đề minh họa của Bộ - thi thử như thi thật
                </p>
              </div>
            </div>
            <div className='flex items-center gap-3 px-4 py-3'>
              <div className='w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0'>
                <Clock className='w-4 h-4 text-amber-500' />
              </div>
              <div>
                <p className='text-sm font-semibold text-gray-900 dark:text-white'>
                  Rèn Bản Lĩnh Phòng Thi
                </p>
                <p className='text-xs text-gray-500 dark:text-gray-400'>
                  Áp lực thời gian thực, bình tĩnh trước mọi dạng bài
                </p>
              </div>
            </div>
            <div className='flex items-center gap-3 px-4 py-3'>
              <div className='w-8 h-8 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0'>
                <TrendingUp className='w-4 h-4 text-red-500' />
              </div>
              <div>
                <p className='text-sm font-semibold text-gray-900 dark:text-white'>
                  Tăng Tốc Điểm Số
                </p>
                <p className='text-xs text-gray-500 dark:text-gray-400'>
                  Chỉ điểm lỗ hổng kiến thức - lấp đầy = điểm cao
                </p>
              </div>
            </div>
            {isPremium && !hasPaid && (
              <div className='flex items-center gap-2 px-4 py-3 bg-amber-50 dark:bg-amber-900/10'>
                <Wallet className='w-4 h-4 text-amber-600 flex-shrink-0' />
                <p className='text-xs text-amber-700 dark:text-amber-400 font-medium'>
                  Đầu tư thông minh: <strong>{exam.price?.toLocaleString()}đ</strong> cho tương lai
                  của bạn
                </p>
              </div>
            )}
          </motion.div>

          {/* Error Message */}
          {error && (
            <div className='mb-6 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-4 py-3 text-sm'>
              {error}
            </div>
          )}

          {/* Trust Signals Strip */}
          <motion.div
            className='mt-4 mb-24 sm:mb-6 flex flex-wrap justify-center gap-x-4 gap-y-2'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            {[
              { icon: CheckCircle, text: '50,000+ học sinh', color: 'text-emerald-500' },
              { icon: Award, text: 'Bản quyền IEQTE', color: 'text-amber-500' },
              { icon: Wallet, text: 'Thanh toán VietQR', color: 'text-blue-500' },
              { icon: Sparkles, text: '95% hài lòng', color: 'text-yellow-500' },
            ].map(({ icon: Icon, text, color }) => (
              <div
                key={text}
                className='flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500'
              >
                <Icon className={`w-3.5 h-3.5 ${color}`} />
                <span>{text}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </motion.main>

      {/* Sticky Bottom CTA Bar  -  mobile only */}
      <motion.div
        className='fixed bottom-0 left-0 right-0 z-40 sm:hidden'
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.6, type: 'spring', stiffness: 300, damping: 30 }}
      >
        <div className='bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-t border-gray-200 dark:border-gray-700 px-4 py-3 shadow-2xl'>
          <div className='flex items-center gap-3'>
            {/* Price info */}
            <div className='flex-shrink-0'>
              {isPremium && !alreadyPaid ? (
                <div>
                  <p className='text-xs text-gray-400 dark:text-gray-500'>Giá</p>
                  <p className='text-base font-bold text-amber-600 dark:text-amber-400'>
                    {exam.price?.toLocaleString()}đ
                  </p>
                </div>
              ) : (
                <div>
                  <p className='text-xs text-gray-400 dark:text-gray-500'>Loại</p>
                  <p className='text-base font-bold text-emerald-600 dark:text-emerald-400'>
                    {alreadyPaid ? 'Đã mua' : 'Miễn phí'}
                  </p>
                </div>
              )}
            </div>

            {/* CTA Button */}
            <div className='flex-1'>
              {hasActiveSession ? (
                <motion.button
                  onClick={handleResume}
                  disabled={checkingSession}
                  whileTap={{ scale: 0.97 }}
                  className='w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-sm shadow-lg disabled:opacity-60 disabled:cursor-not-allowed'
                >
                  <PlayCircle className='w-4 h-4' />
                  {checkingSession ? 'Đang kiểm tra...' : 'Tiếp tục làm bài'}
                </motion.button>
              ) : isPremium ? (
                <motion.button
                  onClick={handleStartClick}
                  disabled={exam.isMock || checkingSession || checkingPayment || preChecking}
                  whileTap={{ scale: 0.97 }}
                  className='w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-primary to-amber-500 text-white font-bold text-sm shadow-lg disabled:opacity-60 disabled:cursor-not-allowed'
                >
                  <Wallet className='w-4 h-4' />
                  {checkingSession || checkingPayment || preChecking
                    ? 'Đang chuẩn bị...'
                    : alreadyPaid
                      ? 'Bắt đầu làm bài'
                      : 'Luyện thi ngay'}
                </motion.button>
              ) : (
                <motion.button
                  onClick={handleStartClick}
                  disabled={starting || exam.isMock || checkingSession || preChecking}
                  whileTap={{ scale: 0.97 }}
                  className='w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-primary to-amber-500 text-white font-bold text-sm shadow-lg disabled:opacity-60 disabled:cursor-not-allowed'
                >
                  <Zap className='w-4 h-4' />
                  {checkingSession || preChecking
                    ? 'Đang chuẩn bị...'
                    : starting
                      ? 'Đang bắt đầu...'
                      : 'Thi thử miễn phí'}
                </motion.button>
              )}
            </div>

            {/* Back button */}
            <motion.button
              onClick={() => navigate('/thi-thu-thpt')}
              whileTap={{ scale: 0.97 }}
              className='flex-shrink-0 p-3 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800'
              aria-label='Quay lại danh sách'
            >
              ←
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Desktop CTA (sm+)  -  hidden on mobile */}
      <div className='hidden sm:block'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12'>
          <div className='flex flex-col sm:flex-row gap-4'>
            {hasActiveSession ? (
              <motion.button
                onClick={handleResume}
                disabled={checkingSession}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className='relative flex-1 inline-flex items-center justify-center gap-3 px-8 py-4 rounded-2xl bg-gradient-to-r from-emerald-500 via-emerald-400 to-teal-500 text-white font-bold text-base shadow-xl transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed'
              >
                <PlayCircle className='w-5 h-5' />
                {checkingSession ? 'Đang kiểm tra...' : 'Tiếp tục làm bài'}
              </motion.button>
            ) : isPremium ? (
              <motion.button
                onClick={handleStartClick}
                disabled={exam.isMock || checkingSession || checkingPayment || preChecking}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className='relative flex-1 inline-flex items-center justify-center gap-3 px-8 py-4 rounded-2xl bg-gradient-to-r from-primary to-amber-500 text-white font-bold text-base shadow-xl transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed'
              >
                <Wallet className='w-5 h-5' />
                {checkingSession || checkingPayment || preChecking
                  ? 'Đang chuẩn bị...'
                  : hasPaid
                    ? 'Bắt đầu làm bài'
                    : `Luyện thi ngay  -  ${exam.price?.toLocaleString()}đ`}
              </motion.button>
            ) : (
              <motion.button
                onClick={handleStartClick}
                disabled={starting || exam.isMock || checkingSession || preChecking}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className='relative flex-1 inline-flex items-center justify-center gap-3 px-8 py-4 rounded-2xl bg-gradient-to-r from-primary to-amber-500 text-white font-bold text-base shadow-xl transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed'
              >
                <Zap className='w-5 h-5' />
                {checkingSession || preChecking
                  ? 'Đang chuẩn bị...'
                  : starting
                    ? 'Đang bắt đầu...'
                    : 'Thi thử miễn phí ngay'}
              </motion.button>
            )}
            <motion.button
              onClick={() => navigate('/thi-thu-thpt')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className='inline-flex items-center justify-center px-6 py-4 rounded-2xl border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 font-semibold bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-300'
            >
              Quay lại danh sách
            </motion.button>
          </div>
        </div>
      </div>

      <UnifiedFooter />

      {/* Payment Dialog */}
      {isPremium && (
        <ModernPaymentDialog
          open={showPaymentDialog}
          onOpenChange={setShowPaymentDialog}
          examTitle={exam.title.normalize('NFC')}
          examTitleVi={exam.title.normalize('NFC')}
          price={exam.price || 0}
          examId={examId}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}

      {/* Exam Start Confirmation Dialog */}
      {exam && (
        <ExamStartConfirmation
          open={showConfirmDialog}
          onOpenChange={setShowConfirmDialog}
          examTitle={exam.title.normalize('NFC')}
          examTitleVi={exam.title.normalize('NFC')}
          duration={exam.durationMinutes || 90}
          questions={exam.totalQuestions || 0}
          isPaid={isPremium || false}
          onConfirm={handleConfirmStart}
        />
      )}
    </div>
  );
}

export default ExamDetailPage;
